// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Captive ball (M2-23, 05 §5.8 DARK MATTER).
//
// A second ball sitting in a short channel with a switch at the back of it. The player's ball
// hits it, it slides down the channel, and if it was hit hard enough it closes the switch and
// bounces back. Every 1990s machine that has one uses it the same way: as the shot that is
// hardest to hit cleanly, because a soft hit does nothing at all.
//
// **It is a device rather than a second `Ball`, and the reason is determinism.** A real second
// ball would need to enter the broadphase, the contact solver's ordering and the ball-pair
// pass, all of which are decided by index (PH-084); a captive ball that appears and disappears
// would shift every other ball's contact order. Instead it is one degree of freedom — how far
// along its channel it has slid — with a spring, a damper, and a proper elastic collision
// against the ball that hits it.
//
// The collision is the real thing, not a scripted bounce: two equal masses exchange momentum
// along the line of centres, so a glancing hit barely moves the captive ball and a square hit
// stops the player's ball dead. That is exactly how the shot feels on a machine, and it is
// what makes the ladder worth climbing.

import { BALL_MASS, BALL_RADIUS } from '../constants'
import { sqrt } from '../../math/deterministic'
import type { Ball } from '../Ball'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { EV_SWITCH_CLOSED } from '../events'
import {CaptiveCage} from './CaptiveCage'

export interface CaptiveBallConfig {
  /** Explicit opt-in keeps historical scored definitions replayable. */
  readonly cageContacts?: boolean
  readonly id: string
  /** Resting centre of the captive ball. */
  readonly centre: readonly [number, number]
  /** How far it can slide before the switch at the back of the channel closes, metres. */
  readonly travel: number
  /** Which way it slides. Normalised on construction. */
  readonly direction: readonly [number, number]
  /**
   * Return-spring stiffness, N/m. A captive ball hangs on a short chain or sits in a sprung
   * cradle; either way it comes back to rest in about a fifth of a second.
   */
  readonly stiffness?: number
  readonly damping?: number
  /** Restitution of the ball-to-ball impact. Two steel balls, so high. */
  readonly restitution?: number
}

/** Steel on steel. Measured coefficients for a pinball pair sit around 0.9. */
const DEFAULT_RESTITUTION = 0.9
const DEFAULT_STIFFNESS = 55
const DEFAULT_DAMPING = 0.9

export class CaptiveBall extends Device {
  readonly kind = 'captiveBall'
  override readonly wantsContact = true

  readonly cx: number
  readonly cy: number
  readonly travel: number
  /** Unit vector along the channel. */
  readonly ux: number
  readonly uy: number

  /** Displacement along the channel, metres. Never negative — the cradle stops it. */
  offset = 0
  /** Speed along the channel, m/s. */
  velocity = 0
  /** Hits that reached the back of the channel since the last reset. */
  hits = 0

  private readonly stiffness: number
  private readonly damping: number
  private readonly restitution: number
  private readonly cage: CaptiveCage | undefined
  /** True while the switch is closed, so one long push counts once. */
  private atBack = false

  protected override ownState(): DeviceState {
    return { offset: this.offset, velocity: this.velocity, hits: this.hits, atBack: this.atBack }
  }

  protected override restoreOwn(state: DeviceState): void {
    this.offset = Number(state['offset'])
    this.velocity = Number(state['velocity'])
    this.hits = Number(state['hits'])
    this.atBack = state['atBack'] === true
  }

  constructor(config: CaptiveBallConfig) {
    super(config.id)
    this.cx = config.centre[0]
    this.cy = config.centre[1]
    this.setOrigin(this.cx, this.cy)
    this.travel = config.travel
    const len = sqrt(config.direction[0] * config.direction[0] + config.direction[1] * config.direction[1])
    if (len === 0) throw new Error(`CaptiveBall ${config.id}: direction has no direction`)
    this.ux = config.direction[0] / len
    this.uy = config.direction[1] / len
    this.cage=config.cageContacts?new CaptiveCage(this.cx,this.cy,this.ux,this.uy,this.travel):undefined
    this.stiffness = config.stiffness ?? DEFAULT_STIFFNESS
    this.damping = config.damping ?? DEFAULT_DAMPING
    this.restitution = config.restitution ?? DEFAULT_RESTITUTION
  }

  /** Current centre of the captive ball. */
  get x(): number {
    return this.cx + this.ux * this.offset
  }
  get y(): number {
    return this.cy + this.uy * this.offset
  }

  override step(_world: World, dt: number): void {
    if (this.offset === 0 && this.velocity === 0) return
    const acc = (-this.stiffness * this.offset) / BALL_MASS - this.damping * this.velocity
    this.velocity += acc * dt
    this.offset += this.velocity * dt
    if (this.offset <= 0) {
      // Back against the cradle: it stops rather than swinging past its rest position.
      this.offset = 0
      if (this.velocity < 0) this.velocity = 0
    }
    if (this.offset < this.travel) this.atBack = false
  }

  override resolveContact(world: World, ball: Ball): boolean {
    const cageHit=this.cage?.resolve(world,ball)??false
    const dx = ball.x - this.x
    const dy = ball.y - this.y
    const dist2 = dx * dx + dy * dy
    const touch = BALL_DIAMETER_SQ
    if (dist2 >= touch || dist2 === 0) return cageHit

    const dist = sqrt(dist2)
    // Normal points from the captive ball to the player's ball.
    const nx = dx / dist
    const ny = dy / dist

    // Separate them first, or the pair sticks and resolves again every iteration.
    const overlap = 2 * BALL_RADIUS - dist
    ball.x += nx * overlap
    ball.y += ny * overlap

    // Approach speed along the line of centres, with the captive ball's own motion — which
    // only exists along its channel — resolved onto it.
    const captiveAlongNormal = this.velocity * (this.ux * nx + this.uy * ny)
    const approach = (ball.vx * nx + ball.vy * ny) - captiveAlongNormal
    if (approach >= 0) return true

    // Equal masses, so the impulse is the classic (1 + e)·v_rel / 2.
    const j = -(1 + this.restitution) * approach * 0.5
    ball.vx += nx * j
    ball.vy += ny * j

    // The captive ball only takes the part of the impulse that points along its channel; the
    // rest goes into the channel walls, which is why an off-line hit is worth so little.
    this.velocity -= j * (this.ux * nx + this.uy * ny)

    if (this.velocity > 0 && !this.atBack) {
      // Will it reach the back? Energy in the spring against the energy it now carries.
      const reach = this.offset + this.velocity * sqrt(BALL_MASS / this.stiffness)
      if (reach >= this.travel) {
        this.atBack = true
        this.hits++
        this.offset = this.travel
        this.velocity = -this.velocity * 0.4
        world.events.emit(EV_SWITCH_CLOSED, this.index, ball.id, world.stepIndex, -approach)
      }
    }
    return true
  }

  override reset(): void {
    this.offset = 0
    this.velocity = 0
    this.atBack = false
    this.hits = 0
  }
}

const BALL_DIAMETER_SQ = (2 * BALL_RADIUS) * (2 * BALL_RADIUS)
